<template>
  <!--
    Ľavý panel podľa roez-ui: tmavý panel cez celú výšku okna, hore logo, v strede navigácia v skupinách,
    dole používateľ s tlačidlom na odhlásenie. Na desktope sa dá zbaliť na úzky pás ikon (⌘B / Ctrl+B),
    na mobile je ten istý obsah v zásuvke.
  -->
  <aside
    class="app-sidebar"
    :class="{ 'is-collapsed': isCollapsed, 'is-mobile': mobile }"
    data-shell="sidebar"
  >
    <div class="sidebar-brand">
      <template v-if="isCollapsed">
        <button
          type="button"
          class="sidebar-brand__toggle"
          aria-label="Rozbaliť navigáciu"
          @click="toggleSidebar"
        >
          <BrandLogo variant="symbol" :width="30" alt="" class="sidebar-brand__mark" />
          <v-icon icon="mdi-dock-left" size="20" class="sidebar-brand__expand" />
          <v-tooltip activator="parent" location="end">Rozbaliť navigáciu (⌘B)</v-tooltip>
        </button>
      </template>
      <template v-else>
        <router-link to="/" class="sidebar-brand__link" aria-label="Optika MANIA — prehľad" @click="onNavigate">
          <BrandLogo :width="92" alt="Optika MANIA" />
        </router-link>
        <span class="sidebar-brand__product">Intranet</span>
        <button
          v-if="mobile"
          type="button"
          class="sidebar-icon-btn"
          aria-label="Zavrieť menu"
          @click="$emit('close')"
        >
          <v-icon icon="mdi-close" size="18" />
        </button>
        <button
          v-else
          type="button"
          class="sidebar-icon-btn"
          aria-label="Zbaliť navigáciu"
          :aria-expanded="true"
          @click="toggleSidebar"
        >
          <v-icon icon="mdi-dock-left" size="18" />
          <v-tooltip activator="parent" location="bottom">Zbaliť navigáciu (⌘B)</v-tooltip>
        </button>
      </template>
    </div>

    <nav class="sidebar-nav" aria-label="Hlavná navigácia">
      <template v-for="group in groups" :key="group.key">
        <div v-if="group.label && !isCollapsed" class="sidebar-group-label">{{ group.label }}</div>
        <div v-else-if="group.label" class="sidebar-group-divider" aria-hidden="true"></div>

        <template v-for="item in group.items" :key="item.to || item.title">
          <span
            v-if="item.disabled"
            class="sidebar-link is-disabled"
            aria-disabled="true"
            :title="`${item.title} — pripravujeme`"
          >
            <span class="sidebar-link__icon"><v-icon :icon="item.icon" size="20" /></span>
            <span class="sidebar-link__label">{{ item.title }}</span>
            <span class="sidebar-link__soon">Čoskoro</span>
          </span>
          <button
            v-else-if="item.action"
            type="button"
            class="sidebar-link"
            :aria-label="isCollapsed ? item.title : undefined"
            @click="runAction(item)"
          >
            <span class="sidebar-link__icon"><v-icon :icon="item.icon" size="20" /></span>
            <span class="sidebar-link__label">{{ item.title }}</span>
            <v-tooltip v-if="isCollapsed" activator="parent" location="end">{{ item.title }}</v-tooltip>
          </button>
          <router-link
            v-else
            :to="item.to"
            class="sidebar-link"
            :class="{ 'is-active': isActive(item), 'sidebar-link--sub': item.sub }"
            :aria-current="isActive(item) ? 'page' : undefined"
            :aria-label="isCollapsed ? item.title : undefined"
            @click="onNavigate"
          >
            <span class="sidebar-link__icon">
              <v-icon :icon="item.icon" :size="item.sub && !isCollapsed ? 16 : 20" />
              <span v-if="isCollapsed && item.badge" class="sidebar-link__dot" aria-hidden="true"></span>
            </span>
            <span class="sidebar-link__label">{{ item.title }}</span>
            <span v-if="item.badge" class="sidebar-link__badge" :aria-label="`${item.badge} nových`">
              {{ item.badge > 99 ? '99+' : item.badge }}
            </span>
            <v-tooltip v-if="isCollapsed" activator="parent" location="end">
              {{ item.title }}<template v-if="item.badge"> · {{ item.badge }}</template>
            </v-tooltip>
          </router-link>
        </template>
      </template>
    </nav>

    <div class="sidebar-foot">
      <router-link to="/profile" class="sidebar-user" :aria-label="`Profil: ${userName}`" @click="onNavigate">
        <span class="sidebar-user__avatar" aria-hidden="true">{{ userInitials }}</span>
        <span class="sidebar-user__text">
          <span class="sidebar-user__name">{{ userName }}</span>
          <span class="sidebar-user__role">{{ userRole }}</span>
        </span>
        <v-tooltip v-if="isCollapsed" activator="parent" location="end">{{ userName }} · Profil</v-tooltip>
      </router-link>
      <!-- Na mobile je zvonček v hornej lište -->
      <NotifikacieZvoncek v-if="!mobile" variant="sidebar" :collapsed="isCollapsed" />
      <button
        type="button"
        class="sidebar-icon-btn sidebar-logout"
        aria-label="Odhlásiť sa"
        :disabled="loggingOut"
        @click="logout"
      >
        <v-progress-circular v-if="loggingOut" indeterminate size="16" width="2" color="white" />
        <v-icon v-else icon="mdi-logout" size="18" />
        <v-tooltip activator="parent" :location="isCollapsed ? 'end' : 'top'">Odhlásiť sa</v-tooltip>
      </button>
    </div>
  </aside>
</template>

<script>
import { computed, defineComponent, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAuthStore } from '@/store/auth'
import BrandLogo from '@/components/brand/BrandLogo.vue'
import NotifikacieZvoncek from '@/components/common/NotifikacieZvoncek.vue'
import { sidebarCollapsed, toggleSidebar, useAppNavigation } from '@/composables/useAppNavigation'

export default defineComponent({
  name: 'AppSidebar',
  components: { BrandLogo, NotifikacieZvoncek },
  props: {
    /** V mobilnej zásuvke: bez zbalenia, s tlačidlom na zatvorenie. */
    mobile: { type: Boolean, default: false },
  },
  emits: ['close', 'navigate'],

  setup(props, { emit }) {
    const route = useRoute()
    const router = useRouter()
    const authStore = useAuthStore()
    const { groups, userName, userInitials, userRole } = useAppNavigation()
    const loggingOut = ref(false)

    const isCollapsed = computed(() => !props.mobile && sidebarCollapsed.value)

    // Položka svieti aj na podstránkach (/novinky/12), okrem tých, ktoré majú presnú zhodu (Prehľad, Manuál).
    const isActive = (item) => {
      if (item.exact) return route.path === item.to
      return route.path === item.to || route.path.startsWith(`${item.to}/`)
    }

    const onNavigate = () => emit('navigate')

    // Položka s akciou (napr. Nahlásiť chybu) — na mobile pritom zavrieme zásuvku
    const runAction = (item) => {
      item.action()
      emit('navigate')
    }

    const logout = async () => {
      loggingOut.value = true
      try {
        await authStore.logout()
        emit('navigate')
        router.push('/login')
      } finally {
        loggingOut.value = false
      }
    }

    return {
      groups,
      userName,
      userInitials,
      userRole,
      isCollapsed,
      isActive,
      onNavigate,
      runAction,
      logout,
      loggingOut,
      toggleSidebar,
    }
  },
})
</script>

<style lang="scss" scoped>
.app-sidebar {
  position: sticky;
  top: 0;
  display: flex;
  flex: none;
  flex-direction: column;
  width: var(--sidebar-width);
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--nav-bg);
  color: var(--nav-text);
  transition: width var(--duration-base) var(--ease-out);
  z-index: 40;

  &.is-collapsed {
    width: var(--sidebar-rail-width);
  }

  &.is-mobile {
    position: relative;
    width: 100%;
  }
}

/* ---- Značka */
.sidebar-brand {
  display: flex;
  flex: none;
  align-items: center;
  gap: 8px;
  min-height: 64px;
  padding: 12px 10px 8px 18px;

  .is-collapsed & {
    justify-content: center;
    padding: 14px 0 8px;
  }
}

.sidebar-brand__link {
  display: flex;
  align-items: center;
  flex: none;
  border-radius: 8px;
}

.sidebar-brand__product {
  flex: 1;
  min-width: 0;
  padding-left: 10px;
  margin-left: 2px;
  border-left: 1px solid #2f2f2f;
  font-size: 15.5px;
  font-weight: 650;
  color: #ffffff;
  letter-spacing: 0;
}

.sidebar-brand__toggle {
  position: relative;
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  color: var(--nav-text);
  transition: background-color var(--duration-fast) ease;

  > * {
    grid-area: 1 / 1;
    transition: opacity var(--duration-fast) ease;
  }

  .sidebar-brand__expand {
    opacity: 0;
  }

  &:hover,
  &:focus-visible {
    background: var(--nav-active);

    .sidebar-brand__mark {
      opacity: 0;
    }
    .sidebar-brand__expand {
      opacity: 1;
    }
  }
}

/* ---- Navigácia */
.sidebar-nav {
  flex: 1;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 4px 10px 16px;
  scrollbar-color: rgba(255, 255, 255, 0.14) transparent;

  &::-webkit-scrollbar-thumb {
    background-color: rgba(255, 255, 255, 0.12);
  }

  .is-collapsed & {
    padding: 4px 12px 16px;
  }
}

.sidebar-group-label {
  padding: 18px 10px 6px;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: var(--nav-muted);
  white-space: nowrap;
}

.sidebar-group-divider {
  height: 1px;
  margin: 10px 8px;
  background: var(--nav-line);
}

.sidebar-link {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  margin-bottom: 2px;
  padding: 0 10px;
  border-radius: 8px;
  color: var(--nav-text);
  font-size: 13.5px;
  font-weight: 450;
  line-height: 20px;
  text-decoration: none;
  white-space: nowrap;
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover {
    background: var(--nav-hover);
    color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid var(--ruby-300);
    outline-offset: -2px;
  }

  &.is-active {
    background: var(--nav-active);
    color: #ffffff;
    font-weight: 600;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.04);

    .sidebar-link__icon {
      color: var(--nav-accent);
    }
  }

  &.is-disabled {
    color: var(--nav-muted);
    opacity: 0.6;
    cursor: not-allowed;

    &:hover {
      background: transparent;
    }
  }

  .is-collapsed & {
    justify-content: center;
    width: 40px;
    padding: 0;
  }
}

/* Podpoložka (napr. Koncepty pod Internými príkazmi): odsadená pod textom nadradenej položky */
.sidebar-link--sub {
  height: 30px;
  padding-left: 32px;
  font-size: 13px;
  color: var(--nav-muted);

  .is-collapsed & {
    padding: 0;
  }
}

/* Položka s akciou je <button> — roztiahneme ho ako odkaz (zbalený panel má 40 px cez .is-collapsed) */
button.sidebar-link {
  width: 100%;
  text-align: left;
}

.sidebar-link__icon {
  position: relative;
  display: grid;
  flex: none;
  place-items: center;
  width: 20px;
  height: 20px;
  color: inherit;
}

.sidebar-link__label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;

  .is-collapsed & {
    display: none;
  }
}

.sidebar-link__badge {
  display: grid;
  flex: none;
  place-items: center;
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  border-radius: 9px;
  background: var(--brand-ruby);
  color: #ffffff;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;

  .is-collapsed & {
    display: none;
  }
}

.sidebar-link__soon {
  flex: none;
  font-size: 11px;
  color: var(--nav-muted);

  .is-collapsed & {
    display: none;
  }
}

.sidebar-link__dot {
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--brand-ruby);
  box-shadow: 0 0 0 2px var(--nav-bg);
}

/* ---- Päta: používateľ a odhlásenie */
.sidebar-foot {
  display: flex;
  flex: none;
  align-items: center;
  gap: 6px;
  padding: 10px;
  border-top: 1px solid var(--nav-line);

  .is-collapsed & {
    flex-direction: column;
    gap: 4px;
    padding: 10px 0 12px;
  }
}

.sidebar-user {
  display: flex;
  flex: 1;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 5px 6px;
  border-radius: 10px;
  color: inherit;
  text-decoration: none;
  transition: background-color var(--duration-fast) ease;

  &:hover,
  &.router-link-active {
    background: var(--nav-hover);
  }

  &:focus-visible {
    outline: 2px solid var(--ruby-300);
    outline-offset: -2px;
  }

  .is-collapsed & {
    flex: none;
    padding: 4px;
  }
}

.sidebar-user__avatar {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 10px;
  background: linear-gradient(140deg, var(--brand-ruby) 0%, var(--ruby-600) 100%);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.02em;
}

.sidebar-user__text {
  display: flex;
  flex-direction: column;
  min-width: 0;

  .is-collapsed & {
    display: none;
  }
}

.sidebar-user__name {
  overflow: hidden;
  font-size: 13px;
  font-weight: 600;
  line-height: 17px;
  color: #ffffff;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-user__role {
  overflow: hidden;
  font-size: 11.5px;
  line-height: 15px;
  color: var(--nav-muted);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-icon-btn {
  display: grid;
  flex: none;
  place-items: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  color: var(--nav-muted);
  transition: background-color var(--duration-fast) ease, color var(--duration-fast) ease;

  &:hover:not(:disabled),
  &:focus-visible {
    background: var(--nav-hover);
    color: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid var(--ruby-300);
    outline-offset: 0;
  }

  &:disabled {
    cursor: wait;
  }
}

.sidebar-brand .sidebar-icon-btn {
  border-radius: 8px;
  width: 30px;
  height: 30px;
}

@media (prefers-reduced-motion: reduce) {
  .app-sidebar {
    transition: none;
  }
}
</style>
